import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import FitScreenIcon from "@mui/icons-material/FitScreen";
import { withTranslation } from "react-i18next";

import { CARD_HEADER_SX } from "@plannel/components/ui/AppCard";
import { FS } from "@plannel/utils/font-util";

/*
  그래프 캔버스 셸 — 제목 줄 + 범례 + 확대/축소·이동.

  ReactFlow 의 Controls 를 쓰지 않는다: 캔버스 내용이 SVG + 절대배치 DOM 한 덩어리라
  "고정 크기 그림을 컨테이너에 맞춰 보여주는" 것 이상이 필요 없고, 그래프 엔진을 얹으면
  노드 배치를 다시 엔진에게 넘겨줘야 하기 때문이다(distNetworkModel 주석 참고).

  줌 배율은 마우스 포인터를 기준으로 잡는다 — 화면 중앙 기준으로 확대하면 보고 있던 노드가
  화면 밖으로 밀려 매번 다시 찾아야 한다.
*/
const MIN_SCALE = 0.25;
const MAX_SCALE = 2.5;
const FIT_MAX_SCALE = 1.25;   // 내용이 작아도 1.25배 이상은 키우지 않는다(선·글자가 뭉개진다)
const FIT_PADDING = 24;

const clampScale = value => Math.min(MAX_SCALE, Math.max(MIN_SCALE, value));

/* 이 거리(px)를 넘겨야 "끌기" 로 본다. 그 아래는 손떨림으로 보고 클릭(선택)으로 넘긴다 */
const DRAG_THRESHOLD_PX = 4;

const NetworkCanvas = ({ t, title, legend, controls, footer, contentWidth, contentHeight, fitKey, onBackgroundClick, children }) => {
  const containerRef = useRef(null);
  const [view, setView] = useState({ scale: 1, x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef(null);

  /*
   * 사용자가 직접 옮기거나 확대한 뒤인지.
   *   아래 ResizeObserver 가 크기 변화마다 fit() 을 불러 화면을 처음 위치로 되돌리고 있었다.
   *   패널 스크롤바가 생기거나 창을 조금만 줄여도 방금 끌어 둔 위치가 튕겨 나가, 화면에서는
   *   "끌었는데 안 움직인다" 로 보인다. 한 번이라도 손을 대면 자동 맞춤을 멈춘다
   *   (fitKey 가 바뀌는 내용 교체 때는 다시 맞춰야 하므로 그때 풀어 준다).
   */
  const userAdjustedRef = useRef(false);

  /* 끌기로 끝난 제스처가 노드 클릭(선택)으로 이어지지 않게 한 번 삼킨다 */
  const suppressClickRef = useRef(false);

  const fit = useCallback(() => {
    const el = containerRef.current;
    if (!el || !contentWidth || !contentHeight) {
      return;
    }
    const cw = el.clientWidth - FIT_PADDING * 2;
    const ch = el.clientHeight - FIT_PADDING * 2;
    if (cw <= 0 || ch <= 0) {
      return;
    }
    const scale = clampScale(Math.min(FIT_MAX_SCALE, Math.min(cw / contentWidth, ch / contentHeight)));
    setView({
      scale,
      x: (el.clientWidth - contentWidth * scale) / 2,
      y: (el.clientHeight - contentHeight * scale) / 2,
    });
  }, [contentWidth, contentHeight]);

  // 내용이 새로 그려지면(뷰 전환·데이터 갱신) 한 번 맞춘다. 사용자가 이후에 옮긴 위치는 건드리지 않는다.
  useLayoutEffect(() => {
    userAdjustedRef.current = false;
    fit();
  }, [fit, fitKey]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return undefined;
    }
    let frame = 0;
    const observer = new ResizeObserver(() => {
      if (userAdjustedRef.current) {
        return;
      }
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(fit);
    });
    observer.observe(el);
    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [fit]);

  // 휠 줌은 페이지 스크롤을 막아야 해서 passive:false 로 직접 붙인다(React onWheel 은 passive).
  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return undefined;
    }
    const onWheel = e => {
      e.preventDefault();
      userAdjustedRef.current = true;
      const rect = el.getBoundingClientRect();
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;
      setView(prev => {
        const next = clampScale(prev.scale * (e.deltaY < 0 ? 1.12 : 1 / 1.12));
        const ratio = next / prev.scale;
        return { scale: next, x: px - (px - prev.x) * ratio, y: py - (py - prev.y) * ratio };
      });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const zoomBy = factor => {
    userAdjustedRef.current = true;
    const el = containerRef.current;
    const px = el ? el.clientWidth / 2 : 0;
    const py = el ? el.clientHeight / 2 : 0;
    setView(prev => {
      const next = clampScale(prev.scale * factor);
      const ratio = next / prev.scale;
      return { scale: next, x: px - (px - prev.x) * ratio, y: py - (py - prev.y) * ratio };
    });
  };

  /*
   * 어디를 눌러도 끌 수 있다.
   *   예전에는 노드 위에서 시작한 끌기를 아예 막았다(선택이 드래그에 먹히지 않게). 그런데 그래프가
   *   빽빽해서 빈 곳을 정확히 짚기 어렵고, 노드를 잡고 끌면 화면이 꿈쩍도 하지 않았다.
   *   대신 움직인 거리로 구분한다 — 문턱을 넘으면 이동, 못 넘으면 클릭(선택)이다.
   */
  const handlePointerDown = e => {
    if (e.button !== 0) {
      return;
    }
    /*
     * 새 제스처가 시작되면 지난 제스처의 흔적을 지운다.
     *   suppressClickRef 는 "다음 click 을 한 번 삼킨다"는 뜻인데, 끌기 뒤에 click 이 오지 않는
     *   경우(포인터가 밖에서 떨어지는 등)에는 그 플래그가 남아 한참 뒤의 멀쩡한 클릭을 잡아먹었다.
     */
    suppressClickRef.current = false;
    dragRef.current = {
      startX: e.clientX, startY: e.clientY,
      originX: view.x, originY: view.y,
      moved: false,
      pointerId: e.pointerId,
    };
    /*
     * 여기서 setPointerCapture 를 하지 않는다.
     *   포인터를 컨테이너에 붙잡아 두면 뒤따르는 click 의 target 이 노드가 아니라 컨테이너가 되어,
     *   노드/선을 눌러도 "빈 곳을 눌렀다"로 처리되어 선택이 풀렸다. 캡처는 문턱을 넘어
     *   실제로 끌기 시작할 때만 건다(그때는 커서가 캔버스 밖으로 나가도 계속 따라와야 한다).
     */
  };

  const handlePointerMove = e => {
    const drag = dragRef.current;
    if (!drag) {
      return;
    }
    /*
     * 버튼이 이미 떨어졌으면 제스처를 접는다.
     *   캡처를 늦게 걸게 되면서, 문턱을 넘기 전에 커서가 캔버스 밖으로 나갔다가 거기서 버튼을 떼면
     *   pointerup 이 이 요소에 오지 않는다. 그대로 두면 다시 들어왔을 때 버튼도 안 눌렀는데
     *   그림이 커서를 따라다닌다.
     */
    if (e.buttons === 0) {
      endDrag(e);
      return;
    }

    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    if (!drag.moved) {
      if (Math.abs(dx) + Math.abs(dy) < DRAG_THRESHOLD_PX) {
        return;
      }
      drag.moved = true;
      userAdjustedRef.current = true;
      setDragging(true);
      // 이 시점부터 진짜 끌기다 — 캔버스 밖으로 나가도 놓치지 않게 여기서 캡처한다.
      e.currentTarget.setPointerCapture?.(e.pointerId);
    }

    setView(prev => ({ ...prev, x: drag.originX + dx, y: drag.originY + dy }));
  };

  const endDrag = e => {
    /* 실제로 끌었으면 뒤따라오는 click 을 삼킨다 — 안 그러면 이동할 때마다 노드가 선택된다 */
    const drag = dragRef.current;
    suppressClickRef.current = Boolean(drag?.moved);
    dragRef.current = null;
    setDragging(false);

    const id = e?.pointerId ?? drag?.pointerId;
    if (id != null && e?.currentTarget?.hasPointerCapture?.(id)) {
      e.currentTarget.releasePointerCapture(id);
    }
  };

  const handleClickCapture = e => {
    if (!suppressClickRef.current) {
      return;
    }
    suppressClickRef.current = false;
    e.stopPropagation();
    e.preventDefault();
  };

  /*
   * 빈 곳을 누르면 선택을 푼다 — 그래프 도구의 관례이자, 이 화면에서 선택을 빠져나올 수 있는
   * 유일한 두 길 중 하나다(다른 하나는 같은 노드를 다시 누르는 것).
   * 끌기로 끝난 제스처는 위 capture 단계에서 이미 삼켜지므로 여기까지 오지 않는다.
   */
  const handleClick = e => {
    if (e.target.closest("[data-network-node]")) {
      return;
    }
    onBackgroundClick?.();
  };

  const zoomButtonSx = { width: 26, height: 26, border: 1, borderColor: "divider", borderRadius: 1 };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, minWidth: 0 }}>
      <Box sx={{
        display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap",
        px: 1.5, py: 1, borderBottom: 1, borderColor: "divider", flexShrink: 0,
      }}>
        <Box sx={{ ...CARD_HEADER_SX, fontSize: FS.TITLE }}>{title}</Box>
        {legend}
        <Box sx={{ flex: 1, minWidth: 8 }} />
        {/* 캔버스에 대해 작용하는 컨트롤(강조 범위 등)은 줌 버튼과 같은 줄에 둔다 —
            페이지 헤더로 올리면 "무엇에 대한 설정인지"가 멀어진다. */}
        {controls}
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Tooltip title={t("zoomIn")}>
            <IconButton size="small" sx={zoomButtonSx} onClick={() => zoomBy(1.2)}>
              <ZoomInIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("zoomOut")}>
            <IconButton size="small" sx={zoomButtonSx} onClick={() => zoomBy(1 / 1.2)}>
              <ZoomOutIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("fitToScreen")}>
            <IconButton size="small" sx={zoomButtonSx} onClick={fit}>
              <FitScreenIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box
        ref={containerRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onClickCapture={handleClickCapture}
        onClick={handleClick}
        sx={{
          flex: 1, minHeight: 0, position: "relative", overflow: "hidden",
          bgcolor: "background.default",
          cursor: dragging ? "grabbing" : "grab",
          touchAction: "none",
        }}
      >
        <Box sx={{
          position: "absolute", left: 0, top: 0,
          width: contentWidth, height: contentHeight,
          transformOrigin: "0 0",
          transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
        }}>
          {children}
        </Box>
      </Box>

      {/* 캔버스에서 빠진 것(품목 필터로 제외된 위치 등)을 알리는 줄. 없으면 자리도 차지하지 않는다. */}
      {footer}
    </Box>
  );
};

export default withTranslation()(NetworkCanvas);
